// src/pages/markets/api/useTraderProfileCache.ts
import { useState, useEffect, useRef } from 'react';
import { apiGet } from './apiClient';
import type { TraderProfile } from './traderProfileTypes';

interface CacheEntry {
    data: TraderProfile;
    fetchedAt: number;
}

/** Module-level cache — shared across all hook instances, survives remounts */
const cache = new Map<string, CacheEntry>();

const DEFAULT_TTL_MS = 5 * 60 * 1000; // 5 minutes
const MAX_CACHE_SIZE = 100;

function evictOldestIfNeeded(): void {
    if (cache.size <= MAX_CACHE_SIZE) return;
    let oldestKey: string | null = null;
    let oldestTime = Infinity;
    for (const [key, val] of cache) {
        if (val.fetchedAt < oldestTime) {
            oldestTime = val.fetchedAt;
            oldestKey = key;
        }
    }
    if (oldestKey) cache.delete(oldestKey);
}

// Non-hook helper for bulk/parallel profile loads (e.g. leaderboards).
// Reads and writes the same module-scoped cache that useTraderProfileCache
// uses, so a detail view opened after a leaderboard load hits warm cache.
export async function getTraderProfile(
    address: string,
    ttlMs: number = DEFAULT_TTL_MS
): Promise<TraderProfile> {
    const entry = cache.get(address);
    if (entry && Date.now() - entry.fetchedAt < ttlMs) {
        return entry.data;
    }
    const data = await apiGet<TraderProfile>(`/api/trader/${address}`);
    cache.set(address, { data, fetchedAt: Date.now() });
    evictOldestIfNeeded();
    return data;
}

export function useTraderProfileCache(
    address: string | undefined,
    ttlMs: number = DEFAULT_TTL_MS
): {
    data: TraderProfile | null;
    loading: boolean;
    refreshing: boolean;
    error: string | null;
} {
    const [data, setData] = useState<TraderProfile | null>(() => {
        if (!address) return null;
        return cache.get(address)?.data ?? null;
    });
    const [loading, setLoading] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const callIdRef = useRef(0);

    useEffect(() => {
        if (!address?.trim()) {
            setData(null);
            setLoading(false);
            setRefreshing(false);
            setError(null);
            return;
        }

        const entry = cache.get(address);
        const now = Date.now();
        const isFresh = entry && now - entry.fetchedAt < ttlMs;

        // Cache hit, still fresh — return cached data, no fetch
        if (isFresh) {
            setData(entry.data);
            setLoading(false);
            setRefreshing(false);
            setError(null);
            return;
        }

        // Cache hit but stale — show old data, background refresh
        if (entry) {
            setData(entry.data);
            setLoading(false);
            setRefreshing(true);
        } else {
            // Cache miss — loading state
            setData(null);
            setLoading(true);
            setRefreshing(false);
        }
        setError(null);

        const callId = ++callIdRef.current;
        const controller = new AbortController();

        apiGet<TraderProfile>(`/api/trader/${address}`, controller.signal)
            .then(res => {
                if (callId !== callIdRef.current) return;
                // Only cache on a successful, non-aborted response.
                cache.set(address, { data: res, fetchedAt: Date.now() });
                evictOldestIfNeeded();
                setData(res);
                setError(null);
            })
            .catch(err => {
                if (err?.name === 'AbortError' || callId !== callIdRef.current)
                    return;
                // On error with stale data, keep showing old data silently
                if (!entry) {
                    setError(err instanceof Error ? err.message : String(err));
                }
            })
            .finally(() => {
                if (callId !== callIdRef.current) return;
                setLoading(false);
                setRefreshing(false);
            });

        return () => controller.abort();
    }, [address, ttlMs]);

    return { data, loading, refreshing, error };
}
